import { useRef, useMemo, forwardRef } from 'react';

import useFlowAnimation from './useFlowAnimation';
import identifiers from 'src/tests/identifiers';

const TRIANGLE_SIZE = 12;
const SVG_MARGIN = 10;
const DISABLED_COLOR = '#D9D9D9';

interface Position {
  x: number;
  y: number;
}

interface FlowLineProps {
  componentId: string;
  start: Position;
  end: Position;
  rate: number;
  cornerNearStart?: boolean;
  color?: string;
  canvasOffset?: {
    top?: number | undefined;
    left?: number | undefined;
    bottom?: number | undefined;
    right?: number | undefined;
  };
  disabled?: boolean;
}

const createBaseTriangle = () => {
  const points = [
    { x: TRIANGLE_SIZE, y: 0 },
    { x: 0, y: TRIANGLE_SIZE / 2 },
    { x: 0, y: -TRIANGLE_SIZE / 2 },
  ];
  return points.map((p) => `${p.x},${p.y}`).join(' ');
};

const calculateSvgDimensions = (start: Position, corner: Position, end: Position) => {
  const xValues = [start.x, corner.x, end.x];
  const yValues = [start.y, corner.y, end.y];

  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  return {
    width: maxX - minX + 2 * SVG_MARGIN,
    height: maxY - minY + 2 * SVG_MARGIN,
    viewBox: `${minX - SVG_MARGIN} ${minY - SVG_MARGIN} ${maxX - minX + 2 * SVG_MARGIN} ${
      maxY - minY + 2 * SVG_MARGIN
    }`,
  };
};

const FlowLine = forwardRef<SVGSVGElement, FlowLineProps>((props: FlowLineProps, ref) => {
  const {
    componentId,
    start,
    end,
    rate,
    cornerNearStart = false,
    canvasOffset = {},
    disabled = false,
    color,
  } = props;
  const lineColor = useMemo(() => (disabled ? DISABLED_COLOR : color), [disabled, color]);
  const baseTrianglePoints = useMemo(() => createBaseTriangle(), []);
  const pathRef = useRef<SVGPathElement>(null);

  const testId = useMemo(() => {
    return (
      (rate < 0 ? identifiers.dashboardPage.flowEdgeReversed : identifiers.dashboardPage.flowEdge) +
      '-' +
      componentId
    );
  }, [rate, componentId]);

  const lineTestId = useMemo(() => {
    return (
      (disabled
        ? identifiers.dashboardPage.flowLineDisabled
        : identifiers.dashboardPage.flowLineEnabled) +
      '-' +
      componentId
    );
  }, [disabled, componentId]);

  const corner = useMemo(
    () => ({
      x: cornerNearStart ? end.x : start.x,
      y: cornerNearStart ? start.y : end.y,
    }),
    [start.x, end.x, start.y, end.y, cornerNearStart]
  );

  const svgDimensions = useMemo(
    () => calculateSvgDimensions(start, corner, end),
    [start, corner, end]
  );

  const pathStr = useMemo(
    () => `M ${start.x} ${start.y} L ${corner.x} ${corner.y} L ${end.x} ${end.y}`,
    [start.x, start.y, corner.x, corner.y, end.x, end.y]
  );

  const svgStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      top: canvasOffset?.top,
      left: canvasOffset?.left,
    }),
    [canvasOffset?.top, canvasOffset?.left]
  );

  const dotPos = useFlowAnimation(pathRef, rate, disabled, pathRef.current?.getTotalLength() || 0);
  const dots = useMemo(() => {
    if (disabled) return null;

    if (!svgDimensions.width || !svgDimensions.height) {
      return null;
    }

    return dotPos.map((pos, index) => (
      <use
        key={`dot-${index}-${pos.x}-${pos.y}-${pos.angle}`}
        href={`#triangle-template-${lineColor}`}
        transform={`translate(${pos.x}, ${pos.y}) rotate(${pos.angle || 0})`}
      />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotPos, disabled, pathRef.current]);

  return (
    <svg
      data-testid={testId}
      ref={ref}
      width={svgDimensions.width}
      height={svgDimensions.height}
      viewBox={svgDimensions.viewBox}
      style={svgStyle}
    >
      <defs>
        <polygon
          id={`triangle-template-${lineColor}`}
          points={baseTrianglePoints}
          fill={lineColor}
          stroke={lineColor}
        />
      </defs>
      <g>
        <path
          ref={pathRef}
          d={pathStr}
          strokeWidth={2}
          fill={'none'}
          stroke={lineColor}
          data-testid={lineTestId}
        />
        {dots}
      </g>
    </svg>
  );
});

export default FlowLine;
